import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Switch } from '@/common/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import ImageUploadModal from '@/common/components/ImageUploadModal'
import GameSelector from '@/common/components/GameSelector'
import { EXCLUDED_GAME_NAMES } from '@/common/constants/excludedGames'
import useMutationCreateShopItem from '@/modules/shop/api/hooks/useMutationCreateShopItem'
import useMutationUpdateShopItem from '@/modules/shop/api/hooks/useMutationUpdateShopItem'
import useMutationGetShopItemImageUploadUrl from '@/core/api/hooks/useMutationGetShopItemImageUploadUrl'
import { FileService } from '@/core/api/services/FileService'
import {
	REWARD_TYPE_OPTIONS,
	CURRENCY_TYPE_OPTIONS,
} from '@/modules/shop/constants'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import { CurrencyType, ShopItemType } from '@/schema'

const shopItemSchema = z
	.object({
		name: z.string().min(1, 'กรุณาระบุชื่อสินค้า'),
		description: z.string().optional(),
		rewardType: z.nativeEnum(ShopItemType, {
			required_error: 'กรุณาเลือกประเภทรางวัล',
		}),
		price: z.number().min(1, 'ราคาต้องมากกว่า 0'),
		amount: z.number().min(1, 'จำนวนต้องมากกว่า 0'),
		currencyType: z.nativeEnum(CurrencyType, {
			required_error: 'กรุณาเลือกประเภทสกุลเงิน',
		}),
		isActive: z.boolean().default(true),
		imageUrl: z.string().optional(),
		// FREE_SPIN specific fields
		presetId: z.string().optional(),
		gameId: z.string().optional(),
		betAmount: z.number().optional(),
	})
	.refine(
		(data) => {
			if (data.rewardType === ShopItemType.FREE_SPIN) {
				return !!data.presetId && !!data.gameId && !!data.betAmount
			}
			return true
		},
		{
			message: 'กรุณากรอกข้อมูล Preset, เกม และจำนวนเดิมพันสำหรับฟรีสปิน',
			path: ['presetId'],
		},
	)

type ShopItemFormData = z.infer<typeof shopItemSchema>

interface ShopItemFormProps {
	mode: 'create' | 'edit'
	itemId?: string
	initialData?: Partial<ShopItemFormData>
	onSuccess?: () => void
}

export default function ShopItemForm({
	mode,
	itemId,
	initialData,
	onSuccess,
}: ShopItemFormProps) {
	const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('')

	// Fetch presets and games for FREE_SPIN
	const { data: presets } = useQueryGetPresetId()
	const { data: gamesData } = useQueryGetGameList({
		providerName: 'PGHARD',
		skip: 0,
		take: 1000,
	})

	// Filter out excluded games by name
	const filteredGames = useMemo(() => {
		if (!gamesData?.data) return []
		return gamesData.data.filter(
			(game) => !EXCLUDED_GAME_NAMES.includes(game.name),
		)
	}, [gamesData])

	const form = useForm<ShopItemFormData>({
		resolver: zodResolver(shopItemSchema),
		defaultValues: {
			currencyType: CurrencyType.DIAMOND,
			rewardType: ShopItemType.CREDIT,
			isActive: true,
			imageUrl: '',
			presetId: undefined,
			gameId: undefined,
			betAmount: undefined,
			amount: undefined,
		},
	})

	// Watch rewardType to lock amount to 1 when FREE_SPIN is selected
	const rewardType = form.watch('rewardType')

	useEffect(() => {
		if (rewardType === ShopItemType.FREE_SPIN) {
			form.setValue('amount', 1)
		}
	}, [rewardType, form])

	const { mutateAsync: getPresignedUrl } =
		useMutationGetShopItemImageUploadUrl()

	const { mutate: createShopItem, isPending: isCreating } =
		useMutationCreateShopItem()
	const { mutate: updateShopItem, isPending: isUpdating } =
		useMutationUpdateShopItem()

	const isPending = isCreating || isUpdating

	// Populate form with initial data when available
	useEffect(() => {
		if (initialData && mode === 'edit') {
			form.reset({
				name: initialData.name || '',
				description: initialData.description || '',
				rewardType: initialData.rewardType || ShopItemType.CREDIT,
				price: initialData.price || undefined,
				amount: initialData.amount || undefined,
				currencyType: initialData.currencyType || CurrencyType.DIAMOND,
				isActive: initialData.isActive ?? true,
				imageUrl: initialData.imageUrl || '',
				presetId: initialData.presetId || undefined,
				gameId: initialData.gameId || undefined,
				betAmount: initialData.betAmount || undefined,
			})
			setUploadedImageUrl(initialData.imageUrl || '')
		}
	}, [initialData, mode, form])

	// Handle image cropping and upload
	const handleImageCropped = async (imageBlob: Blob) => {
		try {
			// Check if the blob is a GIF - upload directly without conversion
			if (imageBlob.type === 'image/gif') {
				// Get presigned URL for GIF
				const urlData = await getPresignedUrl({ fileExtension: 'gif' })

				if (!urlData.presignedUrl || !urlData.fileUrl) {
					throw new Error('Failed to get upload URL')
				}

				// Upload GIF directly to presigned URL (preserves animation)
				const gifFile = new File([imageBlob], `image_${Date.now()}.gif`, {
					type: 'image/gif',
				})
				await FileService.uploadFileToPresignedUrl(
					urlData.presignedUrl,
					gifFile,
				)

				// Update form with the file URL
				const imageUrl = urlData.fileUrl
				form.setValue('imageUrl', imageUrl)
				setUploadedImageUrl(imageUrl)

				toast.success('อัปโหลด GIF สำเร็จ')
			} else {
				// Convert blob to WebP File with 75% quality for other image types
				const webpFile = await convertBlobToWebP(imageBlob, 0.75)

				// Get presigned URL for WebP
				const urlData = await getPresignedUrl({ fileExtension: 'webp' })

				if (!urlData.presignedUrl || !urlData.fileUrl) {
					throw new Error('Failed to get upload URL')
				}

				// Upload WebP file to presigned URL
				await FileService.uploadFileToPresignedUrl(
					urlData.presignedUrl,
					webpFile,
				)

				// Update form with the file URL
				const imageUrl = urlData.fileUrl
				form.setValue('imageUrl', imageUrl)
				setUploadedImageUrl(imageUrl)

				toast.success('รูปภาพถูกอัปโหลดและแปลงเป็น WebP เรียบร้อยแล้ว')
			}
		} catch (error) {
			console.error('Upload failed:', error)
			showErrorToast('ไม่สามารถอัปโหลดรูปภาพได้ กรุณาลองใหม่อีกครั้ง')
			throw error
		}
	}

	// Convert blob to WebP format with specified quality
	const convertBlobToWebP = (
		blob: Blob,
		quality: number = 0.75,
	): Promise<File> => {
		return new Promise((resolve, reject) => {
			const canvas = document.createElement('canvas')
			const ctx = canvas.getContext('2d')
			const img = new Image()

			if (!ctx) {
				reject(new Error('Could not get canvas context'))
				return
			}

			img.onload = () => {
				// Set canvas dimensions to image dimensions
				canvas.width = img.width
				canvas.height = img.height

				// Enable high-quality scaling
				ctx.imageSmoothingEnabled = true
				ctx.imageSmoothingQuality = 'high'

				// Draw image on canvas
				ctx.drawImage(img, 0, 0)

				// Convert to WebP with specified quality
				canvas.toBlob(
					(convertedBlob) => {
						if (convertedBlob) {
							const webpFile = new File(
								[convertedBlob],
								`image_${Date.now()}.webp`,
								{
									type: 'image/webp',
									lastModified: Date.now(),
								},
							)
							resolve(webpFile)
						} else {
							reject(new Error('Failed to convert to WebP'))
						}
					},
					'image/webp',
					quality,
				)
			}

			img.onerror = () => reject(new Error('Failed to load image'))
			img.src = URL.createObjectURL(blob)
		})
	}

	const onSubmit = (data: ShopItemFormData) => {
		const submitData = {
			...data,
			price: data.price || 5,
			imageUrl: data.imageUrl || uploadedImageUrl || undefined,
			description: data.description || undefined,
			currencyType: data.currencyType as CurrencyType,
			// Include FREE_SPIN specific fields if rewardType is FREE_SPIN
			...(data.rewardType === ShopItemType.FREE_SPIN && {
				presetId: data.presetId,
				gameId: data.gameId,
				betAmount: data.betAmount,
			}),
		}

		if (mode === 'create') {
			createShopItem(submitData, {
				onSuccess: () => {
					form.reset()
					setUploadedImageUrl('')
					onSuccess?.()
				},
			})
		} else if (mode === 'edit' && itemId) {
			updateShopItem(
				{ id: itemId, data: submitData },
				{
					onSuccess: () => {
						onSuccess?.()
					},
				},
			)
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{mode === 'create' ? 'ข้อมูลสินค้า' : 'แก้ไขข้อมูลสินค้า'}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>ชื่อสินค้า</FormLabel>
									<FormControl>
										<Input placeholder="ระบุชื่อสินค้า" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>รายละเอียด</FormLabel>
									<FormControl>
										<Textarea placeholder="ระบุรายละเอียดสินค้า" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="rewardType"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>ประเภทรางวัล</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกประเภทรางวัล" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{REWARD_TYPE_OPTIONS.map((option) => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="amount"
								render={({ field }) => {
									const isFreeSpin =
										form.watch('rewardType') === ShopItemType.FREE_SPIN
									return (
										<FormItem>
											<FormLabel required>
												<span className="text-sm text-muted-foreground">
													จำนวนรางวัล{' '}
													{isFreeSpin && (
														<p className="text-sm text-muted-foreground">
															ฟรีสปินจำกัดจำนวนรางวัลที่ 1 อันเท่านั้น
														</p>
													)}
												</span>
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="0"
													{...field}
													value={isFreeSpin ? 1 : field.value}
													disabled={isFreeSpin}
													onNumberChange={(value) => field.onChange(value)}
												/>
											</FormControl>

											<FormMessage />
										</FormItem>
									)
								}}
							/>
						</div>

						{/* Conditional FREE_SPIN fields */}
						{form.watch('rewardType') === ShopItemType.FREE_SPIN && (
							<>
								<FormField
									control={form.control}
									name="presetId"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>RTP Preset (สำหรับฟรีสปิน)</FormLabel>
											<Select
												onValueChange={field.onChange}
												value={field.value}
											>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder="เลือก RTP Preset" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{presets?.presets.map((preset) => (
														<SelectItem key={preset.id} value={preset.id}>
															{preset.note}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="gameId"
									render={({ field }) => (
										<GameSelector
											field={field}
											games={filteredGames}
											label="เกม (PGHARD เท่านั้น)"
											placeholder="เลือกเกม"
											required
										/>
									)}
								/>

								<FormField
									control={form.control}
									name="betAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>ราคาฟรีสปิน</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="0"
													{...field}
													onNumberChange={(value) => field.onChange(value)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</>
						)}

						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="currencyType"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>ประเภทสกุลเงิน</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกประเภทสกุลเงิน" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{CURRENCY_TYPE_OPTIONS.map((option) => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="price"
								render={({ field }) => {
									const currencyType = form.watch('currencyType')
									const currencyLabel =
										CURRENCY_TYPE_OPTIONS.find(
											(option) => option.value === currencyType,
										)?.label || 'เพชร'

									return (
										<FormItem>
											<FormLabel required>ราคา ({currencyLabel})</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="0"
													{...field}
													onNumberChange={(value) => field.onChange(value)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)
								}}
							/>
						</div>

						<FormField
							control={form.control}
							name="imageUrl"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<ImageUploadModal
											label="รูปภาพสินค้า"
											buttonText="อัปโหลดรูปภาพสินค้า"
											aspectRatio={1}
											maxFileSize={5 * 1024 * 1024}
											acceptedFileTypes={[
												'image/jpeg',
												'image/png',
												'image/webp',
												'image/gif',
											]}
											initialImageUrl={uploadedImageUrl || field.value}
											onImageCropped={handleImageCropped}
											required
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="isActive"
							render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
									<div className="space-y-0.5">
										<FormLabel className="text-base">เปิดใช้งาน</FormLabel>
										<div className="text-sm text-muted-foreground">
											สินค้าจะแสดงในร้านค้าหรือไม่
										</div>
									</div>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
								</FormItem>
							)}
						/>

						<div className="flex gap-4">
							<Button type="submit" disabled={isPending}>
								{isPending
									? mode === 'create'
										? 'กำลังสร้าง...'
										: 'กำลังอัปเดต...'
									: mode === 'create'
										? 'สร้างสินค้า'
										: 'อัปเดตสินค้า'}
							</Button>
							{mode === 'create' && (
								<Button
									type="button"
									variant="outline"
									onClick={() => {
										form.reset()
										setUploadedImageUrl('')
									}}
								>
									ล้างข้อมูล
								</Button>
							)}
						</div>
					</form>
				</Form>
			</CardContent>
		</Card>
	)
}
